/**
 * Impression-based view tracking for cards in lists/grids — the web
 * equivalent of the mobile FlashList viewability config (50% visible for
 * ~300ms → one `trackView` per entity per session).
 *
 * One shared IntersectionObserver (threshold 0.5) watches every registered
 * card. When a card stays at least half visible for the 300ms dwell time, its
 * view is handed to `trackView` and the element is unobserved. `trackView`
 * dedupes per entity per session, so scrolling back and forth doesn't inflate
 * counts, and it only QUEUES the view: services/tracking.ts sends queued views
 * together in one `trackViews` request (about one per 1.5s of scrolling, or
 * per 50 views), not one request per card.
 *
 * Usage (inside a card component):
 *
 *   const impressionRef = useImpressionRef('brand', brand.id);
 *   return <article ref={impressionRef}>…</article>;
 *
 * The returned ref callback is stable per (entity, id) pair; React calls it
 * with null on unmount, which unregisters the element automatically. Invalid
 * ids and environments without IntersectionObserver are safely ignored.
 */
import { useCallback, useRef } from 'react';
import { trackView, type TrackableEntity } from '@/services/tracking';

const DWELL_MS = 300;
const VISIBILITY_THRESHOLD = 0.5;

interface Registration {
  entity: TrackableEntity;
  id: string;
  timer: number | null;
}

const registrations = new Map<Element, Registration>();
let sharedObserver: IntersectionObserver | null = null;

function unregister(el: Element): void {
  const reg = registrations.get(el);
  if (reg?.timer != null) window.clearTimeout(reg.timer);
  registrations.delete(el);
  sharedObserver?.unobserve(el);
}

function getObserver(): IntersectionObserver | null {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
    return null;
  }
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const reg = registrations.get(entry.target);
          if (!reg) continue;
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD) {
            // Start the dwell timer — only count if it stays visible.
            if (reg.timer === null) {
              const el = entry.target;
              reg.timer = window.setTimeout(() => {
                trackView(reg.entity, reg.id);
                unregister(el); // tracked (or deduped) — stop observing
              }, DWELL_MS);
            }
          } else if (reg.timer !== null) {
            // Scrolled away before the dwell time elapsed — cancel.
            window.clearTimeout(reg.timer);
            reg.timer = null;
          }
        }
      },
      { threshold: VISIBILITY_THRESHOLD },
    );
  }
  return sharedObserver;
}

/**
 * Returns a ref callback that registers the element with the shared
 * impression observer. Attach it to the card's root element.
 */
export function useImpressionRef(
  entity: TrackableEntity,
  id: string | undefined | null,
): (node: Element | null) => void {
  const currentEl = useRef<Element | null>(null);

  return useCallback(
    (node: Element | null) => {
      if (currentEl.current) {
        unregister(currentEl.current);
        currentEl.current = null;
      }
      if (!node || !id) return;
      const observer = getObserver();
      if (!observer) return;
      currentEl.current = node;
      registrations.set(node, { entity, id, timer: null });
      observer.observe(node);
    },
    [entity, id],
  );
}
